import { Injectable, inject } from '@angular/core';
import {
  HttpClient,
  HttpErrorResponse,
  HttpHeaders,
} from '@angular/common/http';
import { map, catchError } from 'rxjs/operators';
import { Observable, throwError } from 'rxjs';
import { SessionStorageService } from '../../services/session-storage.service';
import { EnvironmentService } from '../../services/environment.service';
import {
  Template,
  ProgramSearchResponse,
  TemplateResponse,
  FundSearch,
} from '../../models/account-models';
import { ApiResponse } from '../../models/general-models';

@Injectable({
  providedIn: 'root',
})
export class AccountsService {
  private http = inject(HttpClient);
  private ss = inject(SessionStorageService);
  private envSvc = inject(EnvironmentService);

  private environment = this.envSvc.get();
  private baseUrl: string = this.environment.apiBase;
  private app: string = this.ss.get('currentApp') || 'pp';
  private org: string = this.environment.org;

  private getRequestHeaders(): HttpHeaders {
    const session = this.ss.get('session');
    return new HttpHeaders()
      .set('X-RB-S', session)
      .set('X-RB-I', 'iw')
      .set('X-RB-O', this.org);
  }

  updateTemplates(templates: Template[]): Observable<TemplateResponse> {
    const url = `${this.baseUrl}/Investment/Template`;
    return this.http
      .patch<TemplateResponse>(url, templates, {
        headers: this.getRequestHeaders(),
      })
      .pipe(
        map(res => res),
        catchError(this.handleError)
      );
  }

  saveNewTemplate(templates: Template): Observable<TemplateResponse> {
    const url = `${this.baseUrl}/Investment/Template`;
    return this.http
      .post<TemplateResponse>(url, templates, {
        headers: this.getRequestHeaders(),
      })
      .pipe(
        map(res => res),
        catchError(this.handleError)
      );
  }

  deleteTemplate(templateId: string): Observable<TemplateResponse> {
    const url = `${this.baseUrl}/Investment/Template?templateID=${templateId}`;
    return this.http
      .delete<TemplateResponse>(url, { headers: this.getRequestHeaders() })
      .pipe(
        map(res => res),
        catchError(this.handleError)
      );
  }

  programSearch(
    includeProductShelf = false
  ): Observable<ProgramSearchResponse> {
    let url = `${this.baseUrl}/Investment/AvailablePrograms`;
    if (includeProductShelf) {
      url += '?includeProductShelf=true';
    }
    return this.http
      .get<ProgramSearchResponse>(url, { headers: this.getRequestHeaders() })
      .pipe(
        map(res => res),
        catchError(this.handleError)
      );
  }

  copyTemplate(templateId: string): Observable<TemplateResponse> {
    const url = `${this.baseUrl}/Investment/CopyTemplate?templateID=${templateId}`;
    return this.http
      .get<TemplateResponse>(url, { headers: this.getRequestHeaders() })
      .pipe(
        map(res => res),
        catchError(this.handleError)
      );
  }

  private handleError(error: HttpErrorResponse): Observable<never> {
    return throwError(() => error.error || 'Server error');
  }
}
